import { useReducer } from 'react';

import { produce } from 'immer';
import _ from 'lodash';

export default function useUserFilters() {
  const [userFilters, dispatch] = useReducer(reducer, defaultUserFilters);

  function handleUserFilterAdd(filterKey) {
    dispatch({
      type: 'ADD_FILTER',
      payload: { filterKey },
    });
  }

  function handleUserFilterRemove(filterKey) {
    dispatch({
      type: 'REMOVE_FILTER',
      payload: { filterKey },
    });
  }

  function handleUserFilterValueAdd({ filterKey, key, value }) {
    dispatch({
      type: 'ADD_FILTER_VALUE',
      payload: { filterKey, key, value },
    });
  }

  function handleUserFilterValueRemove({ filterKey, key, value }) {
    dispatch({
      type: 'REMOVE_FILTER_VALUE',
      payload: { filterKey, key, value },
    });
  }

  function handleUserFilterValueChange({ filterKey, key, value }) {
    dispatch({
      type: 'CHANGE_FILTER_VALUE',
      payload: { filterKey, key, value },
    });
  }

  function handleUserFiltersCleanup() {
    dispatch({ type: 'CLEANUP' });
  }

  return {
    handleUserFilterAdd,
    handleUserFilterRemove,
    handleUserFilterValueAdd,
    handleUserFilterValueRemove,
    handleUserFilterValueChange,
    handleUserFiltersCleanup,
    userFilters,
  };
}

function reducer(state, action) {
  return produce(state, (draftState) => {
    switch (action.type) {
      case 'ADD_FILTER': {
        const { filterKey } = action.payload;
        if (!(filterKey in draftState)) {
          draftState[filterKey] = defaultFilterValues[filterKey];
        }
        break;
      }
      case 'REMOVE_FILTER': {
        const { filterKey } = action.payload;
        delete draftState[filterKey];
        break;
      }
      case 'ADD_FILTER_VALUE': {
        const { filterKey, key, value } = action.payload;
        draftState[filterKey][key].push(value);
        break;
      }
      case 'REMOVE_FILTER_VALUE': {
        const { filterKey, key, value } = action.payload;
        const valueIndex = draftState[filterKey][key].findIndex(
          (id) => id === value,
        );
        if (valueIndex !== -1) draftState[filterKey][key].splice(valueIndex, 1);
        break;
      }
      case 'CHANGE_FILTER_VALUE': {
        const { filterKey, key, value } = action.payload;
        draftState[filterKey][key] = value;
        break;
      }
      case 'CLEANUP': {
        Object.keys(state).forEach((filterKey) => {
          if (_.isEqual(state[filterKey], defaultFilterValues[filterKey])) {
            delete draftState[filterKey];
          }
        });
        break;
      }
    }
  });
}

export const allowanceTypes = [
  'fullUser',
  'personnelReporteeManager',
  'ramsViewer',
  'noPermissions',
];

const defaultFilterValues = {
  allowanceTypeFilter: Object.fromEntries(
    allowanceTypes.map((allowanceType) => [allowanceType, false]),
  ),
  divisionFilter: { selectedIds: [] },
};

const defaultUserFilters = {};

export function checkedAllowanceTypes(allowanceTypeFilter) {
  return Object.keys(allowanceTypeFilter).filter(
    (allowanceType) => allowanceTypeFilter[allowanceType],
  );
}

export function buildUserFilters({ isDivisionFilterable, translations }) {
  const filterKeys = Object.keys(defaultFilterValues).filter(
    (filterKey) => isDivisionFilterable || filterKey !== 'divisionFilter',
  );

  return new Map(
    filterKeys
      .map((filterKey) => [filterKey, translations[filterKey]])
      .sort((a, b) => (_.lowerCase(a[1]) > _.lowerCase(b[1]) ? 1 : -1)),
  );
}
